/**
 * DV-TEMP-002: Heartbeats Panel Component
 * Display live and recent heartbeat status for all stages
 */

import { useMemo, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Loader2,
  AlertCircle,
  Pause
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useHeartbeats } from '@/hooks/useHeartbeats';
import type { StageHeartbeat } from '@/lib/api-types';
import type { StageName } from '@/lib/shared-types';
import { HEARTBEAT_STALE_THRESHOLD } from './HeartbeatsDemo';
import { getDemoError, logDemoError } from '@/lib/demo-error-handler';
import { PermissionNotice, NotFoundNotice, ServerErrorNotice, ErrorBanner } from './ErrorNotices';
import { useErrorRetry } from '@/hooks/useErrorRetry';
import type { LogEntry } from './LogsPanel';

export interface HeartbeatsPanelProps {
  executionId: string;
  selectedStage?: StageName;
  onStageSelect?: (stage: StageName) => void;
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Calculate time since last heartbeat
const getTimeSince = (timestamp: string): string => {
  try {
    const now = new Date();
    const heartbeatTime = new Date(timestamp);
    const diffMs = now.getTime() - heartbeatTime.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
    return `${Math.floor(diffSecs / 3600)}h ago`;
  } catch {
    return 'unknown';
  }
};

// Get status indicator for heartbeat
const getStatusIndicator = (heartbeat: StageHeartbeat, isStale: boolean) => {
  if (isStale) {
    return <AlertTriangle className="w-4 h-4" style={{ color: 'var(--warning)' }} />;
  }

  if (!heartbeat.isActive) {
    return <Pause className="w-4 h-4" style={{ color: 'var(--fg-muted)' }} />;
  }

  if (heartbeat.progress >= 100) {
    return <CheckCircle className="w-4 h-4" style={{ color: 'var(--success)' }} />;
  }

  return <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--accent)' }} />;
};

interface HeartbeatRowProps {
  heartbeat: StageHeartbeat;
  isSelected: boolean;
  isStale: boolean;
  onSelect?: () => void;
  testId?: string;
}

function HeartbeatRow({ heartbeat, isSelected, isStale, onSelect, testId }: HeartbeatRowProps) {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && onSelect) {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      className={cn(
        "p-3 border-b last:border-b-0 transition-colors",
        onSelect && "cursor-pointer hover:bg-opacity-50 focus:outline-none focus:ring-2 focus:ring-inset",
        isSelected && "border-l-2"
      )}
      style={{
        borderColor: 'var(--border-subtle)',
        backgroundColor: isSelected ? 'var(--bg-elevated)' : 'transparent',
        borderLeftColor: isSelected ? 'var(--accent)' : 'transparent',
        ['--hover-bg' as any]: 'var(--bg-interactive-hover)',
        ['--focus-ring' as any]: '0 0 0 2px var(--accent)',
      }}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      tabIndex={onSelect ? 0 : -1}
      role={onSelect ? "button" : undefined}
      aria-label={onSelect ? `Select ${heartbeat.stage} stage` : undefined}
      aria-current={isSelected ? 'true' : undefined}
      onMouseEnter={(e) => {
        if (onSelect) {
          e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isSelected ? 'var(--bg-elevated)' : 'transparent';
      }}
      onFocus={(e) => {
        if (onSelect) {
          e.currentTarget.style.boxShadow = 'var(--focus-ring)';
        }
      }}
      onBlur={(e) => {
        if (onSelect) {
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
      data-testid={testId ? `${testId}-row-${heartbeat.stage}` : undefined}
    >
      <div className="flex items-center justify-between gap-4">
        {/* Stage Info */}
        <div className="flex items-center gap-3 min-w-0">
          {getStatusIndicator(heartbeat, isStale)}
          <div className="min-w-0">
            <div
              className="font-mono text-sm font-medium capitalize truncate"
              style={{
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--fg-primary)'
              }}
            >
              {heartbeat.stage}
            </div>
            <div
              className="text-xs truncate"
              style={{ color: 'var(--fg-muted)' }}
            >
              Iteration {heartbeat.iteration}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div className="flex-shrink-0 text-right min-w-0">
          <div
            className="text-sm font-mono"
            style={{
              fontFamily: 'var(--font-family-mono)',
              color: heartbeat.progress >= 100 ? 'var(--success)' : 'var(--accent)'
            }}
          >
            {heartbeat.progress}%
          </div>
          <div
            className="text-xs"
            style={{ color: 'var(--fg-muted)' }}
          >
            {getTimeSince(heartbeat.lastHeartbeat)}
          </div>
        </div>
      </div>

      {/* Message */}
      {heartbeat.message && (
        <div
          className="mt-2 text-xs truncate"
          style={{ color: 'var(--fg-secondary)' }}
          title={heartbeat.message}
        >
          {heartbeat.message}
        </div>
      )}

      {/* Progress Bar */}
      <div
        className="mt-2 h-1 rounded-full overflow-hidden"
        style={{ backgroundColor: 'var(--bg-surface)' }}
      >
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${Math.min(100, Math.max(0, heartbeat.progress))}%`,
            backgroundColor: heartbeat.progress >= 100 ? 'var(--success)' : 'var(--accent)'
          }}
        />
      </div>
    </div>
  );
}

export function HeartbeatsPanel({
  executionId,
  selectedStage,
  onStageSelect,
  onLog,
  className,
  'data-testid': testId = 'heartbeats-panel'
}: HeartbeatsPanelProps) {
  const { data: heartbeatsData, isLoading, isError, error, refetch } = useHeartbeats(executionId);

  // Extract demo error and log structured errors
  const demoError = isError && error ? getDemoError(error) : null;
  if (demoError && onLog) {
    // Log structured error only once per error instance
    const errorKey = `heartbeats-${demoError.correlationId || demoError.status}-${executionId}`;
    if (!sessionStorage.getItem(errorKey)) {
      onLog(logDemoError(demoError));
      sessionStorage.setItem(errorKey, 'logged');
    }
  }

  // Auto-retry for server errors
  const { retryState, scheduleRetry, canRetry, forceRetry } = useErrorRetry({
    maxRetries: 3,
    onRetryAttempt: (count) => {
      onLog?.({
        id: `retry-${Date.now()}`,
        ts: new Date().toISOString(),
        level: 'info',
        text: `Retrying heartbeats request (attempt ${count})`,
        meta: { executionId, endpoint: '/heartbeats' }
      });
    }
  });

  // Schedule retry for server errors
  useEffect(() => {
    if (demoError && demoError.type === 'server_error' && canRetry(demoError)) {
      scheduleRetry(demoError, refetch);
    }
  }, [demoError, scheduleRetry, canRetry, refetch]);

  // Calculate which heartbeats are stale using consistent threshold
  const staleHeartbeats = useMemo(() => {
    if (!heartbeatsData?.heartbeats) return new Set();

    const now = new Date();

    return new Set(
      heartbeatsData.heartbeats
        .filter(hb => {
          const heartbeatTime = new Date(hb.lastHeartbeat);
          return now.getTime() - heartbeatTime.getTime() > HEARTBEAT_STALE_THRESHOLD;
        })
        .map(hb => hb.stage)
    );
  }, [heartbeatsData?.heartbeats]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn("border rounded-lg p-4", className)}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)'
        }}
        data-testid={`${testId}-loading`}
      >
        <div className="flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--fg-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading heartbeats...
        </div>
      </div>
    );
  }

  // Error state with DV-TEMP-003 enhanced error handling
  if (isError && demoError) {
    if (demoError.type === 'permission_denied') {
      return (
        <PermissionNotice
          error={demoError}
          onRetry={demoError.retryable ? refetch : undefined}
          className={className}
          data-testid={`${testId}-error`}
        />
      );
    }

    if (demoError.type === 'not_found') {
      return (
        <NotFoundNotice
          error={demoError}
          executionId={executionId}
          onRetry={demoError.retryable ? refetch : undefined}
          className={className}
          data-testid={`${testId}-error`}
        />
      );
    }

    if (demoError.type === 'server_error') {
      return (
        <div>
          {/* Show retry banner if auto-retry is active */}
          {retryState.isRetrying && (
            <ErrorBanner
              error={demoError}
              onRetry={() => forceRetry(refetch)}
              className="mb-4"
              data-testid={`${testId}-retry-banner`}
            />
          )}
          <ServerErrorNotice
            error={demoError}
            onRetry={() => forceRetry(refetch)}
            className={className}
            data-testid={`${testId}-error`}
          />
        </div>
      );
    }

    // Fallback error display
    return (
      <div
        className={cn("border rounded-lg p-4", className)}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--danger)'
        }}
        data-testid={`${testId}-error`}
      >
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--danger)' }}>
          <AlertCircle className="w-4 h-4" />
          {demoError.userMessage}
        </div>
      </div>
    );
  }

  // Empty state
  if (!heartbeatsData?.heartbeats.length) {
    return (
      <div
        className={cn("border rounded-lg p-4 text-center", className)}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)'
        }}
        data-testid={`${testId}-empty`}
      >
        <Activity className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--fg-muted)' }} />
        <div className="text-sm" style={{ color: 'var(--fg-muted)' }}>
          No heartbeats available
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("border rounded-lg overflow-hidden", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)'
      }}
      data-testid={testId}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b flex items-center justify-between"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-subtle)'
        }}
      >
        <h3
          className="text-sm font-mono uppercase tracking-wider flex items-center gap-2"
          style={{
            fontFamily: 'var(--font-family-mono)',
            color: 'var(--accent)'
          }}
        >
          <Activity className="w-4 h-4" />
          Stage Heartbeats
        </h3>

        {/* Execution Status */}
        <div
          className="text-xs px-2 py-1 rounded border"
          style={{
            backgroundColor: heartbeatsData.status === 'running' ? 'var(--success)' : 'var(--warning)',
            borderColor: heartbeatsData.status === 'running' ? 'var(--success)' : 'var(--warning)',
            color: 'var(--bg-app)',
            fontFamily: 'var(--font-family-mono)'
          }}
        >
          {heartbeatsData.status.toUpperCase()}
        </div>
      </div>

      {/* Heartbeats List */}
      <div className="max-h-96 overflow-auto">
        {heartbeatsData.heartbeats.map((heartbeat) => (
          <HeartbeatRow
            key={heartbeat.stage}
            heartbeat={heartbeat}
            isSelected={heartbeat.stage === selectedStage}
            isStale={staleHeartbeats.has(heartbeat.stage)}
            onSelect={onStageSelect ? () => onStageSelect(heartbeat.stage as StageName) : undefined}
            testId={testId}
          />
        ))}
      </div>

      {/* Warning for non-running execution */}
      {heartbeatsData.status !== 'running' && (
        <div
          className="px-4 py-2 border-t flex items-center gap-2 text-xs"
          style={{
            backgroundColor: 'var(--warning)',
            borderColor: 'var(--border-subtle)',
            color: 'var(--bg-app)'
          }}
        >
          <AlertTriangle className="w-3 h-3" />
          Execution is {heartbeatsData.status} - heartbeats may be stale
        </div>
      )}
    </div>
  );
}